import Box from "@material-ui/core/Box";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import React, { Ref, useContext, useEffect, useImperativeHandle, useState } from "react";
import ActionFlow from "../../../../../api/models/test-series/ActionFlow";
import ProductActionValueGroup from "../../../../../api/models/test-series/ProductActionValueGroup";
import SubstrateActionValueGroup from "../../../../../api/models/test-series/SubstrateActionValueGroup";
import SubstrateSection from "../../../../../api/models/test-series/SubstrateSection";
import TestSeriesDTO from "../../../../../api/models/TestSeriesDTO";
import CustomButton from "../../../../../components/Button/CustomButton";
import ConfirmationDialogContext from "../../../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import CustomTextField from "../../../../../components/Input/CustomTextField";
import ErrorSnackbarContext from "../../../../../contexts/ErrorSnackbarContext";
import { TestSeriesContext } from "../../../../../contexts/TestSeriesContext";
import HiddenError from "../../../../../models/HiddenError";
import { tesaGrey1, tesaGrey5, tesaRed } from "../../../../../styles/colors";
import TestSeriesValidator from "../../../../../validator/TestSeriesValidator";
import AnalyzeHistoricalResultsDialog from "../../AnalyzeHistoricalResultsDialog";
import { removeActiveFilter } from "../TestCasesTab/utils/ActiveFilterLocalStorage";
import TestSeriesMeasurementColumn from "./columns/TestSeriesMeasurementColumn";
import TestSeriesProductColumn from "./columns/TestSeriesProductColumn";
import TestSeriesSpecimenColumn from "./columns/TestSeriesSpecimenColumn";
import TestSeriesSubstrateColumn from "./columns/TestSeriesSubstrateColumn";
import TestSeriesProductHeader from "./headers/TestSeriesProductHeader";
import TestSeriesSubstrateHeader from "./headers/TestSeriesSubstrateHeader";
import getHistoricalButtonText from "../../utils/HistoricalButtonLabel";
import TestSeriesDialogContext from "../TestSeriesTabbedDialogContext";
import CustomProgress from "../../../../../components/Alert/CustomProgress";
import { AnalyzeHistoricalFormType } from "../../AnalyzeHistoricalForm";
import ConfidentialConfirmationDialog from "../../../../../components/Dialog/ConfidentialConfirmationDialog";
import {
  containsHighlyConfidentialInfo,
  fetchHighlyConfidentialRegex,
} from "../../../../../functions/confidentialCheck.function";

const SUFFIX_DEFAULT_IDENTIFIER = "#";

const useStyles = makeStyles({
  checkboxLabel: {
    height: "36px",
    marginRight: 0,
    "& .MuiCheckbox-root": {
      paddingBottom: 0,
      paddingTop: 0,
    },
    "& .MuiFormControlLabel-label": {
      lineHeight: 1,
    },
  },
  columnHeadline: {
    color: "black",
  },
  label: {
    color: tesaGrey1,
    fontFamily: "Proxima Nova Bold",
    fontSize: "1rem",
    fontColor: "Black",
    border: "1px solid",
    borderColor: tesaGrey5,
  },
  table: {
    minWidth: 650,
    fontFamily: "Proxima Nova Bold Condensed",
    fontSize: "5rem !important",
    lineHeight: "2rem",
    border: "1px solid",
    borderColor: tesaGrey5,
    marginTop: "20px",
  },
  textInput: {
    marginBottom: "10px",
  },
});

const extractNumericalSuffix = (suffix: string, identifier: string): number => {
  return parseInt(suffix.split(identifier)[1], 10);
};

const findMaxSuffix = <ActionGroup extends { actionFlow?: ActionFlow | null }>(
  actionGroups: ActionGroup[],
  suffixIdentifier: string,
): number => {
  const suffixMax = actionGroups.reduce<number>((max, currGroup) => {
    const suffix = currGroup?.actionFlow?.nameSuffix;
    const suffixNumber = suffix && extractNumericalSuffix(suffix, suffixIdentifier);
    if (suffixNumber && !Number.isNaN(suffixNumber)) {
      return suffixNumber > max ? suffixNumber : max;
    }
    return max;
  }, 0);
  return suffixMax;
};

const generateNextSuffix = <ActionGroup extends { actionFlow?: ActionFlow | null }>(
  actionGroups: ActionGroup[],
  suffixIdentifier: string,
): string => {
  const max = findMaxSuffix(actionGroups, suffixIdentifier);
  return `${suffixIdentifier}${max === 0 || max === 1 ? 2 : max + 1}`;
};

interface TestSeriesTableProps {
  generateTestCaseTable: (testSeries: TestSeriesDTO) => Promise<void>;
  updateTestSeriesApiData: (testSeries: TestSeriesDTO) => Promise<void>;
  initialTestSeriesString: string;
  historicalSearchSettings: AnalyzeHistoricalFormType | null;
}

export interface TestSeriesTableRef {
  isDirty: () => boolean;
  isNew: () => boolean;
  save: () => Promise<void>;
  saveAndGenerate: () => Promise<void>;
}

const TestSeriesTable = (props: TestSeriesTableProps, ref: Ref<TestSeriesTableRef> | null) => {
  const classes = useStyles();

  const { generateTestCaseTable, updateTestSeriesApiData, initialTestSeriesString, historicalSearchSettings } = props;
  const {
    permissions,
    selectedTestSeriesObject,
    showTextDialog,
    updateSelectedTestSeriesObject,
    updateSelectedTestSeriesId,
    updateSelectedTestSeriesHasTestCases,
  } = useContext(TestSeriesContext);

  const { loadingHistoricalTestCases, historicalTestCasesMatchCounter } = useContext(TestSeriesDialogContext);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { clearSnackbar, showErrorSnackbar, handleHttpError } = useContext(ErrorSnackbarContext);

  const enabled = permissions.edit || false;

  const [substrateOne, setSubstrateOne] = useState(
    selectedTestSeriesObject.substrateSections
      ? selectedTestSeriesObject?.substrateSections[0]
      : ({} as SubstrateSection),
  );
  const [substrateTwo, setSubstrateTwo] = useState(
    selectedTestSeriesObject.substrateSections
      ? selectedTestSeriesObject?.substrateSections[1]
      : ({} as SubstrateSection),
  );
  const [product, setProduct] = useState(selectedTestSeriesObject.productSection);
  const [specimen, setSpecimen] = useState(selectedTestSeriesObject.specimenSection);
  const [measurement, setMeasurement] = useState(selectedTestSeriesObject.measurementSection);
  const [condition, setCondition] = useState(selectedTestSeriesObject.conditionSection);
  const [note, setNote] = useState(selectedTestSeriesObject.note || "");
  const [isThirdParty, setIsThirdParty] = useState(selectedTestSeriesObject.testLocation === "THIRD_PARTY_LAB");
  const [validated, setValidated] = useState(false);
  const [substrateCount, setSubstrateCount] = useState(0);
  const [testCasesGenerated, setTestCasesGenerated] = useState(selectedTestSeriesObject?.testCasesCount > 0);

  const [isAnalyzeHistoricalResultsDialogOpen, setIsAnalyzeHistoricalResultsDialogOpen] = useState(false);
  const [showConfidentialDialog, setShowConfidentialDialog] = useState(false);
  const [pendingTestSeries, setPendingTestSeries] = useState<TestSeriesDTO | null>(null);
  const [pendingAction, setPendingAction] = useState<"save" | "saveAndGenerate" | null>(null);
  const [confidentialRegex, setConfidentialRegex] = useState<RegExp | null>(null);

  const cellCount = 3 + substrateCount;
  const cellSizeInPercent = Math.floor(100 / cellCount);

  const dirtyCheck = (objectToCompare: TestSeriesDTO) => {
    const currentTestSeries = JSON.stringify({
      ...objectToCompare,
      testCases: undefined,
    });
    return currentTestSeries !== initialTestSeriesString;
  };

  const showGenerateDialog = (onConfirm: () => Promise<void>): Promise<void> => {
    if (!testCasesGenerated) {
      return onConfirm();
    }
    return showConfirmationDialog({
      headline: "Test table will be generated again",
      text: "Previous changes to the Test Table will be overwritten.",
      confirmText: "Continue",
      confirmColor: tesaRed,
    }).then(onConfirm);
  };

  const generateTestSeriesObj = (): TestSeriesDTO => {
    const substrateCountTmp = selectedTestSeriesObject.substrateSections.length;

    const newTestSeries: TestSeriesDTO = { ...selectedTestSeriesObject };
    if (substrateCountTmp > 1) {
      newTestSeries.substrateSections[0] = substrateOne;
      newTestSeries.substrateSections[1] = substrateTwo;
    }
    if (substrateCountTmp === 1) {
      newTestSeries.substrateSections[0] = substrateTwo;
    }
    newTestSeries.productSection = product;
    newTestSeries.specimenSection = specimen;
    newTestSeries.measurementSection = measurement;
    newTestSeries.conditionSection = condition;
    newTestSeries.note = note !== "" ? note : null;
    newTestSeries.testLocation = isThirdParty ? "THIRD_PARTY_LAB" : "CSC_LAB";

    return newTestSeries;
  };

  const save = (testSeries: TestSeriesDTO): Promise<void> => {
    removeActiveFilter(testSeries.id);
    updateSelectedTestSeriesObject(testSeries);
    updateSelectedTestSeriesId(testSeries.id);
    return updateTestSeriesApiData(testSeries);
  };

  const saveAndGenerateTestCases = (testSeries: TestSeriesDTO): Promise<void> => {
    return save(testSeries)
      .then(() => generateTestCaseTable(testSeries))
      .then(() => {
        setTestCasesGenerated(true);
        updateSelectedTestSeriesHasTestCases(true);
      });
  };

  const validateSaveAndGenerate = (testSeries: TestSeriesDTO): Promise<void> => {
    setValidated(true);
    const validationResult = TestSeriesValidator.validate(testSeries);
    if (validationResult.valid) {
      return showGenerateDialog(() => saveAndGenerateTestCases(testSeries));
    }
    clearSnackbar();
    validationResult.errors.forEach(showErrorSnackbar);
    return Promise.reject(new HiddenError("Validation failed."));
  };

  const saveTestSeries = (): Promise<void> => {
    const newTestSeries = generateTestSeriesObj();
    
    // Check for confidential information in note
    if (confidentialRegex && newTestSeries.note && containsHighlyConfidentialInfo([newTestSeries.note], confidentialRegex)) {
      setPendingTestSeries(newTestSeries);
      setPendingAction("save");
      setShowConfidentialDialog(true);
      return Promise.resolve();
    }
    
    if (testCasesGenerated) {
      return validateSaveAndGenerate(newTestSeries);
    }
    return save(newTestSeries);
  };

  const validateAndSaveTestSeries = (): Promise<void> => {
    const newTestSeries = generateTestSeriesObj();
    
    // Check for confidential information in note
    if (confidentialRegex && newTestSeries.note && containsHighlyConfidentialInfo([newTestSeries.note], confidentialRegex)) {
      setPendingTestSeries(newTestSeries);
      setPendingAction("saveAndGenerate");
      setShowConfidentialDialog(true);
      return Promise.resolve();
    }
    
    return validateSaveAndGenerate(newTestSeries);
  };

  const addSampleSuffix = <
    ActionGroup extends {
      actionFlow?: ActionFlow | null | undefined;
    },
  >(
    newGroup: ActionGroup,
    prevGroups: ActionGroup[],
  ): Promise<ActionGroup | void> => {
    const matches = prevGroups.filter(
      (group) => group.actionFlow && newGroup.actionFlow && group.actionFlow.name === newGroup.actionFlow.name,
    );
    if (matches.length === 0) {
      return Promise.resolve(newGroup);
    }
    return showTextDialog(
      "Enter Suffix",
      generateNextSuffix(matches, SUFFIX_DEFAULT_IDENTIFIER),
      "Please enter a suffix to further describe the additional sample",
      { disabledOnEmpty: true, disabledHint: "Please enter a suffix" },
    )
      .then((nameSuffix) => {
        if (newGroup.actionFlow) {
          newGroup.actionFlow.nameSuffix = nameSuffix;
        }
        return newGroup;
      })
      .catch(() => {
        // do nothing
      });
  };

  const handleAddSubstrateOne = (newSubstrate: SubstrateActionValueGroup): void => {
    addSampleSuffix(newSubstrate, substrateOne.substrateActionFlows).then((withSuffix) => {
      if (withSuffix) {
        setSubstrateOne({
          ...substrateOne,
          substrateActionFlows: [...substrateOne.substrateActionFlows, withSuffix],
        });
      }
    });
  };

  const handleAddSubstrateTwo = (newSubstrate: SubstrateActionValueGroup): void => {
    addSampleSuffix(newSubstrate, substrateTwo.substrateActionFlows).then((withSuffix) => {
      if (withSuffix) {
        setSubstrateTwo({
          ...substrateTwo,
          substrateActionFlows: [...substrateTwo.substrateActionFlows, withSuffix],
        });
      }
    });
  };

  const handleAddProduct = (newProduct: ProductActionValueGroup): void => {
    addSampleSuffix(newProduct, product.productActionFlows).then((withSuffix) => {
      if (withSuffix) {
        setProduct({
          ...product,
          productActionFlows: [...product.productActionFlows, withSuffix],
        });
      }
    });
  };

  const handleNoteChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setNote(e.target.value);
  };

  // Handle reference interface events
  useImperativeHandle(ref, () => ({
    isNew: (): boolean => {
      return !testCasesGenerated;
    },
    isDirty: (): boolean => {
      return enabled && dirtyCheck(generateTestSeriesObj());
    },
    save: (): Promise<void> => {
      return saveTestSeries();
    },
    saveAndGenerate: (): Promise<void> => {
      return validateAndSaveTestSeries();
    },
  }));

  useEffect(() => {
    fetchHighlyConfidentialRegex().then(setConfidentialRegex);
  }, []);

  useEffect(() => {
    const substrateCountTmp = selectedTestSeriesObject.substrateSections?.length;
    setSubstrateCount(substrateCountTmp);

    if (substrateCountTmp > 1) {
      setSubstrateOne(selectedTestSeriesObject.substrateSections[0]);
      setSubstrateTwo(selectedTestSeriesObject.substrateSections[1]);
    }
    if (substrateCountTmp === 1) {
      setSubstrateTwo(selectedTestSeriesObject.substrateSections[0]);
    }
    setProduct(selectedTestSeriesObject.productSection);
    setSpecimen(selectedTestSeriesObject.specimenSection);
  }, [selectedTestSeriesObject]);

  if (loadingHistoricalTestCases) {
    return <CustomProgress />;
  }

  return (
    <>
      {isAnalyzeHistoricalResultsDialogOpen && (
        <AnalyzeHistoricalResultsDialog
          testMethodName={selectedTestSeriesObject.testMethodReference.testMethodName!}
          onClose={() => {
            setIsAnalyzeHistoricalResultsDialogOpen(false);
          }}
          open={isAnalyzeHistoricalResultsDialogOpen}
          settingsCached={historicalSearchSettings}
        />
      )}
      {showConfidentialDialog && pendingTestSeries && (
        <ConfidentialConfirmationDialog
          objectName="note"
          open={showConfidentialDialog}
          onCancel={() => {
            setShowConfidentialDialog(false);
            setPendingTestSeries(null);
            setPendingAction(null);
          }}
          onProceed={() => {
            setShowConfidentialDialog(false);
            if (pendingAction === "saveAndGenerate") {
              validateSaveAndGenerate(pendingTestSeries).catch(handleHttpError);
            } else if (testCasesGenerated) {
              validateSaveAndGenerate(pendingTestSeries).catch(handleHttpError);
            } else {
              save(pendingTestSeries).catch(handleHttpError);
            }
            setPendingTestSeries(null);
            setPendingAction(null);
          }}
        />
      )}
      <div>
        {Object.entries(selectedTestSeriesObject).length !== 0 && (
          <div>
            <Grid container spacing={4}>
              <Grid item md={6} xs={12}>
                <CustomTextField
                  autoFocus
                  fullWidth
                  disabled={!enabled}
                  name="note"
                  id="note"
                  label="Note"
                  value={note}
                  type="nart"
                  onChange={handleNoteChange}
                />
              </Grid>
              <Grid container item md={6} xs={12} justify="flex-end" alignItems="flex-end">
                <FormControlLabel
                  className={classes.checkboxLabel}
                  control={
                    <Checkbox
                      checked={isThirdParty}
                      disabled={!enabled}
                      onChange={() => setIsThirdParty((prev) => !prev)}
                    />
                  }
                  label="3rd Party Test"
                />
                {enabled && (
                  <Box marginLeft={3}>
                    <CustomButton primary text="Save" onClick={() => saveTestSeries().catch(handleHttpError)} />
                  </Box>
                )}
              </Grid>
              <Grid item md={6} xs={12}>
                <CustomButton
                  disabled={!historicalTestCasesMatchCounter}
                  text={getHistoricalButtonText(historicalTestCasesMatchCounter)}
                  onClick={() => {
                    setIsAnalyzeHistoricalResultsDialogOpen(true);
                  }}
                />
              </Grid>
            </Grid>
            <TableContainer>
              <Table className={classes.table} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    {substrateCount > 1 && (
                      <TestSeriesSubstrateHeader
                        linkedTestSeriesParent={selectedTestSeriesObject.linkedTestSeriesParent}
                        enabled={enabled}
                        substrateNumber="1"
                        substrate={substrateOne}
                        updateSubstrate={setSubstrateOne}
                        cellSizeInPercent={cellSizeInPercent}
                        handleAddSubstrate={handleAddSubstrateOne}
                      />
                    )}
                    {substrateCount > 0 && (
                      <TestSeriesSubstrateHeader
                        linkedTestSeriesParent={selectedTestSeriesObject.linkedTestSeriesParent}
                        enabled={enabled}
                        substrateNumber={substrateCount > 1 ? " 2" : ""}
                        substrate={substrateTwo}
                        updateSubstrate={setSubstrateTwo}
                        cellSizeInPercent={cellSizeInPercent}
                        handleAddSubstrate={handleAddSubstrateTwo}
                      />
                    )}
                    <TestSeriesProductHeader
                      enabled={enabled}
                      product={product}
                      cellSizeInPercent={cellSizeInPercent}
                      handleAddProduct={handleAddProduct}
                    />
                    <TableCell width={`${cellSizeInPercent}%`} align="left" className={classes.label}>
                      <b className={classes.columnHeadline}>Specimen</b>
                    </TableCell>
                    <TableCell width={`${cellSizeInPercent}%`} align="left" className={classes.label}>
                      <b className={classes.columnHeadline}>Measurement</b>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    {substrateCount > 1 && (
                      <TestSeriesSubstrateColumn
                        enabled={enabled}
                        validated={validated}
                        substrateNumber="1"
                        substrate={substrateOne}
                        updateSubstrate={setSubstrateOne}
                      />
                    )}
                    {substrateCount > 0 && (
                      <TestSeriesSubstrateColumn
                        enabled={enabled}
                        validated={validated}
                        substrateNumber={substrateCount > 1 ? " 2" : ""}
                        substrate={substrateTwo}
                        updateSubstrate={setSubstrateTwo}
                      />
                    )}
                    <TestSeriesProductColumn
                      enabled={enabled}
                      validated={validated}
                      product={product}
                      updateProduct={setProduct}
                    />
                    <TestSeriesSpecimenColumn
                      enabled={enabled}
                      validated={validated}
                      specimen={specimen}
                      updateSpecimen={setSpecimen}
                    />
                    <TestSeriesMeasurementColumn
                      enabled={enabled}
                      validated={validated}
                      measurement={measurement}
                      updateMeasurement={setMeasurement}
                      condition={condition}
                      updateCondition={setCondition}
                    />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}
      </div>
    </>
  );
};

export default React.forwardRef(TestSeriesTable);
